import React from "react";
import { useSearchParams } from "react-router-dom";
import {
  ActivityIndicator,
  ScrollView,
  Text,
  View,
  useWindowDimensions,
} from "react-native";

import type { TNormalizedClinicAsset } from "../../../common/hooks/clinicAssets";
import { useClinicAssets } from "../../../common/hooks/useClinicAssets";
import { SecondaryButton, TitleText } from "../../../common/native";
import { MultiImageUpload, useImagePreviewUrls } from "../../../common/web";
import ImageFilterIcon from "../../../common/web/ImageFilterIcon.web";
import {
  MAX_FILE_SIZE,
  VALID_IMAGE_TYPES,
} from "../../../common/web/MultiImageUpload.web";
import { STICKER_PRESETS } from "../../../../config/stickerPresets";
import { SessionContext } from "../../../../contexts";
import {
  downloadImageToolResult,
  generateCensorshipUploadUrls,
  startImageToolExport,
  startImageFiltersJob,
  TCensorshipJobMetadata,
  TImageFiltersRequest,
  uploadCensorshipImages,
  waitForCensorshipJob,
  waitForImageToolExport,
} from "../../../../services/api-image-tools";
import { APP_TOKENS as tokens } from "../../../../styles/tokens";
import {
  isDebugQueryEnabled,
  isDebugQueryMode,
} from "../../../../utils/debugQuery";
import {
  downloadEditedCensorshipImage,
  TCensorshipStickerPlacement,
} from "./CensorshipResultEditor.web";
import { styles } from "./ImageToolsPage.styles";
import {
  ImageToolsFilterPicker,
  FaceCensorshipModePicker,
  ImageToolsProcessingStatus,
  ImageToolsResults,
  ImageToolsStickerLibrary,
  TCoverOption,
  TFaceCensorshipMode,
  TImageFilterId,
  TImageToolsJobResult,
} from "./ImageToolsSections";
import type { TFaceBlurRegion } from "./FaceBlurResultEditor.web";
import { IMAGE_TOOLS_TOKENS as imageToolsTokens } from "./tokens";
import { getCensorshipDownloadPath } from "./censorshipDownloadPath";
import {
  buildOrderedImageToolExportOperations,
  buildImageToolsProcessingRequest,
  DEFAULT_TATTOO_BLUR_RADIUS,
  type TOrderedImageTool,
} from "./imageToolExportOperations";
import {
  canRecoverWithManualFaceCensorship,
  getFaceDetectionResultSlots,
} from "./faceCensorshipWarnings";
import { ImageToolsExportHistory } from "./ImageToolsExportHistory";

const MAX_IMAGES = 50;
const MAX_IMAGE_HEIGHT = 1600;
const IMAGE_TOOLS_SAMPLE_DEBUG_MODE = "image-tools-sample";
const DEBUG_SAMPLE_IMAGE_URL =
  "https://pub-0991182fdab143cf938953a8d6ab8813.r2.dev/user-app-assets/before-after-samples/before_front.png";
const DEBUG_BATCH_ID = "image-tools-debug";
const DEBUG_CENSORSHIP_BBOXES: NonNullable<
  NonNullable<
    TCensorshipJobMetadata["images"][number]["final_bboxes_on_original"]
  >
> = {
  chest: [118, 188, 266, 276],
  groin: [142, 376, 242, 472],
};

const COVER_OPTIONS: TCoverOption[] = STICKER_PRESETS.map(
  ({ id, label, url }) => ({ id, label, url }),
);

type TProcessingState =
  | "idle"
  | "uploading"
  | "queued"
  | "processing"
  | "complete"
  | "error";

type TDebugLoadState = "idle" | "loading" | "ready" | "error";
type TDownloadTarget = number | "all" | null;

const ImageToolsPage: React.FC = () => {
  const session = React.useContext(SessionContext)?.session;
  const [searchParams, setSearchParams] = useSearchParams();
  const isDebugMode = isDebugQueryMode(
    searchParams,
    IMAGE_TOOLS_SAMPLE_DEBUG_MODE,
  );
  const showRegionDebugLabels = isDebugQueryEnabled(searchParams);
  const forceLocalCanvas =
    process.env.EXPO_PUBLIC_ENV !== "production" &&
    searchParams.get("forceLocalCanvas") === "true";
  const { width } = useWindowDimensions();
  const isMobile = width < imageToolsTokens.size.mobileBreakpoint;
  const activeControllerRef = React.useRef<AbortController | null>(null);
  const exportControllerRef = React.useRef<AbortController | null>(null);
  const [selectedImages, setSelectedImages] = React.useState<Blob[]>([]);
  const selectedImagePreviewUrls = useImagePreviewUrls(selectedImages);
  const {
    assets: clinicStickers,
    isLoading: isStickerLibraryLoading,
    isUploading: isAddingSticker,
    deletingAssetId: deletingStickerId,
    load: loadClinicAssets,
    upload: uploadClinicAsset,
    remove: removeClinicAsset,
  } = useClinicAssets({
    session,
    initialLoading: !isDebugMode,
  });
  const [stickerLibraryError, setStickerLibraryError] = React.useState("");
  const [stickerToDelete, setStickerToDelete] =
    React.useState<TNormalizedClinicAsset | null>(null);
  const [selectedCoverId, setSelectedCoverId] = React.useState<string>(
    COVER_OPTIONS[0].id,
  );
  const [selectedFilters, setSelectedFilters] = React.useState<
    TImageFilterId[]
  >(["cover-privates"]);
  const [faceCensorshipMode, setFaceCensorshipMode] =
    React.useState<TFaceCensorshipMode>("blur");
  const [processingState, setProcessingState] =
    React.useState<TProcessingState>("idle");
  const [isPreparingImages, setIsPreparingImages] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [error, setError] = React.useState("");
  const [result, setResult] = React.useState<TImageToolsJobResult | null>(null);
  const [placementsByImage, setPlacementsByImage] = React.useState<
    Record<number, TCensorshipStickerPlacement[]>
  >({});
  const [faceStickerPlacementsByImage, setFaceStickerPlacementsByImage] =
    React.useState<Record<number, TCensorshipStickerPlacement[]>>({});
  const [faceBlurRegionsByImage, setFaceBlurRegionsByImage] = React.useState<
    Record<number, TFaceBlurRegion[]>
  >({});
  const [editedImages, setEditedImages] = React.useState<
    Record<number, boolean>
  >({});
  const [downloadingIndex, setDownloadingIndex] = React.useState<number | null>(
    null,
  );
  const [isDownloadingAll, setIsDownloadingAll] = React.useState(false);
  const [downloadTarget, setDownloadTarget] =
    React.useState<TDownloadTarget>(null);
  const [isHighQualityExporting, setIsHighQualityExporting] =
    React.useState(false);
  const [debugLoadState, setDebugLoadState] =
    React.useState<TDebugLoadState>("idle");
  const [debugLoadError, setDebugLoadError] = React.useState("");
  const [debugReloadKey, setDebugReloadKey] = React.useState(0);
  const [exportHistoryRefreshKey, setExportHistoryRefreshKey] =
    React.useState(0);

  const isBusy =
    processingState === "uploading" ||
    processingState === "queued" ||
    processingState === "processing";
  const isCoverFilterSelected = selectedFilters.includes("cover-privates");
  const isFaceCensorshipSelected = selectedFilters.includes("hide-faces");
  const needsStickerPicker =
    isCoverFilterSelected ||
    (isFaceCensorshipSelected && faceCensorshipMode === "sticker");
  const coverOptions = React.useMemo<TCoverOption[]>(
    () => [
      ...COVER_OPTIONS,
      ...clinicStickers.map((sticker, index) => ({
        id: `clinic-${sticker.asset_id}`,
        label: `Clinic sticker ${index + 1}`,
        url: sticker.get_url,
        assetId: sticker.asset_id,
      })),
    ],
    [clinicStickers],
  );

  const fetchStickerLibrary = React.useCallback(async () => {
    setStickerLibraryError("");
    try {
      await loadClinicAssets();
    } catch {
      setStickerLibraryError(
        "Your clinic sticker library could not be loaded. Retry to see your saved stickers.",
      );
    }
  }, [loadClinicAssets]);

  const toggleFilter = (filterId: TImageFilterId) => {
    if (isBusy) return;
    setSelectedFilters((current) => {
      if (current.includes(filterId)) {
        return current.filter((id) => id !== filterId);
      }
      return [...current, filterId];
    });
    setResult(null);
    setPlacementsByImage({});
    setFaceStickerPlacementsByImage({});
    setFaceBlurRegionsByImage({});
    setProcessingState("idle");
    setError("");
  };

  React.useEffect(
    () => () => {
      activeControllerRef.current?.abort();
      exportControllerRef.current?.abort();
    },
    [],
  );

  React.useEffect(() => {
    if (isDebugMode) {
      return;
    }
    void fetchStickerLibrary();
  }, [fetchStickerLibrary, isDebugMode]);

  React.useEffect(() => {
    if (!isDebugMode) {
      setDebugLoadState("idle");
      setDebugLoadError("");
      return;
    }

    const controller = new AbortController();
    activeControllerRef.current?.abort();
    activeControllerRef.current = null;
    setDebugLoadState("loading");
    setDebugLoadError("");
    setSelectedImages([]);
    setResult(null);
    setPlacementsByImage({});
    setFaceStickerPlacementsByImage({});
    setFaceBlurRegionsByImage({});
    setEditedImages({});
    setProcessingState("idle");
    setUploadProgress(0);
    setError("");

    const loadDebugSample = async () => {
      try {
        const response = await window.fetch(DEBUG_SAMPLE_IMAGE_URL, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`The sample image returned HTTP ${response.status}.`);
        }
        const sampleImage = await response.blob();
        if (controller.signal.aborted) return;

        setSelectedImages([sampleImage]);
        setResult({
          batchId: DEBUG_BATCH_ID,
          downloadUrls: [`${DEBUG_BATCH_ID}-result`],
          appliedFilters: ["cover-privates"],
          coverImageUrl: COVER_OPTIONS[0].url,
          coverImageLabel: COVER_OPTIONS[0].label,
          metadata: {
            success: true,
            errors: null,
            images: [
              {
                input_s3_key: "debug/before_front.png",
                output_s3_key: "debug/before_front-censored.png",
                final_bboxes_on_original: DEBUG_CENSORSHIP_BBOXES,
              },
            ],
          },
        });
        setProcessingState("complete");
        setDebugLoadState("ready");
      } catch (caughtError) {
        if (controller.signal.aborted) return;
        setDebugLoadState("error");
        setDebugLoadError(
          caughtError instanceof Error
            ? `${caughtError.message} Retry the debug sample.`
            : "The debug sample could not be loaded. Retry the debug sample.",
        );
      }
    };

    void loadDebugSample();
    return () => controller.abort();
  }, [debugReloadKey, isDebugMode]);

  const handleSelectedImagesChange = (images: Blob[]) => {
    setSelectedImages(images);
    setResult(null);
    setPlacementsByImage({});
    setFaceStickerPlacementsByImage({});
    setFaceBlurRegionsByImage({});
    setEditedImages({});
    setProcessingState("idle");
    setUploadProgress(0);
    setError("");
  };

  const handleAddSticker = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!session) {
      setStickerLibraryError(
        "Your clinic could not be identified. Please sign in again.",
      );
      return;
    }
    if (!VALID_IMAGE_TYPES.includes(file.type)) {
      setStickerLibraryError(
        "Choose a PNG or JPEG image to add to the sticker library.",
      );
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setStickerLibraryError(
        "The sticker is larger than 50 MB. Choose a smaller image.",
      );
      return;
    }

    setStickerLibraryError("");
    try {
      const nextSticker = await uploadClinicAsset(file);
      setSelectedCoverId(`clinic-${nextSticker.asset_id}`);
    } catch {
      setStickerLibraryError(
        "The sticker could not be added. Check the image and try again.",
      );
    }
  };

  const handleDeleteSticker = async () => {
    if (!session || !stickerToDelete || deletingStickerId !== null) return;

    const sticker = stickerToDelete;
    setStickerLibraryError("");
    try {
      await removeClinicAsset(sticker.asset_id);
      setSelectedCoverId((current) =>
        current === `clinic-${sticker.asset_id}`
          ? COVER_OPTIONS[0].id
          : current,
      );
      setStickerToDelete(null);
    } catch {
      setStickerLibraryError(
        "The sticker could not be deleted. Please try again.",
      );
      setStickerToDelete(null);
    }
  };

  const handleGenerate = async () => {
    if (!selectedImages.length || !selectedFilters.length || isBusy) return;

    if (!session?.clinicId) {
      setError("Your clinic could not be identified. Please sign in again.");
      return;
    }

    const cover = needsStickerPicker
      ? coverOptions.find((option) => option.id === selectedCoverId)
      : undefined;

    if (needsStickerPicker && !cover) {
      setError("Choose a censorship cover and try again.");
      return;
    }

    const aiFilters: TImageFiltersRequest = buildImageToolsProcessingRequest({
      selectedFilters,
      coverAssetUrl: cover?.url,
    });

    activeControllerRef.current?.abort();
    const controller = new AbortController();
    activeControllerRef.current = controller;

    setError("");
    setResult(null);
    setPlacementsByImage({});
    setFaceStickerPlacementsByImage({});
    setFaceBlurRegionsByImage({});
    setEditedImages({});
    setUploadProgress(0);

    try {
      setProcessingState("uploading");
      const uploadData = await generateCensorshipUploadUrls(
        session.clinicId,
        selectedImages.length,
        controller.signal,
      );
      await uploadCensorshipImages({
        files: selectedImages as File[],
        uploadUrls: uploadData.upload_urls,
        signal: controller.signal,
        onProgress: (completed, total) =>
          setUploadProgress(Math.round((completed / total) * 100)),
      });

      setProcessingState("queued");
      const job = await startImageFiltersJob({
        clinicId: session.clinicId,
        batchId: uploadData.batch_id,
        numImages: selectedImages.length,
        aiFilters,
        signal: controller.signal,
      });

      setProcessingState("processing");
      const initialMetadataResponse = await window
        .fetch(job.metadata_url, {
          method: "GET",
          cache: "no-store",
          signal: controller.signal,
        })
        .catch(() => null);
      const metadata = initialMetadataResponse?.ok
        ? await initialMetadataResponse.json()
        : await waitForCensorshipJob({
            metadataUrl: job.metadata_url,
            signal: controller.signal,
          });

      const canRecoverManually = canRecoverWithManualFaceCensorship({
        metadataSuccess: metadata.success,
        isFaceCensorshipSelected,
        isCoverFilterSelected,
      });
      if (!metadata.success && !canRecoverManually) {
        const reason = metadata.errors
          ? Object.values(metadata.errors).join(" ")
          : "The image processor did not complete this job.";
        throw new Error(`${reason} Please retry the images.`);
      }
      if (
        isFaceCensorshipSelected &&
        isCoverFilterSelected &&
        (metadata.images.length !== selectedImages.length ||
          metadata.images.some(
            (image: TCensorshipJobMetadata["images"][number]) =>
              image.ai_filters?.censorship?.mode_used !== "detect" ||
              !Array.isArray(image.ai_filters.censorship.regions),
          ))
      ) {
        throw new Error(
          "Private-area detection metadata is missing. Please retry the batch before export.",
        );
      }
      const resultDownloadUrls = getFaceDetectionResultSlots({
        downloadUrls: job.download_urls,
        imageCount: selectedImages.length,
        isFaceDetectionJob: isFaceCensorshipSelected,
      });
      if (!resultDownloadUrls) {
        throw new Error(
          "The processor did not return every filtered image. Please retry the batch.",
        );
      }

      setResult({
        batchId: job.batch_id,
        downloadUrls: resultDownloadUrls,
        metadata,
        appliedFilters: [...selectedFilters],
        coverImageUrl: cover?.url,
        coverImageLabel: cover?.label,
        coverAssetId: cover?.assetId,
        faceCensorshipMode: isFaceCensorshipSelected
          ? faceCensorshipMode
          : undefined,
      });
      if (canRecoverManually) {
        setError(
          "Automatic face detection could not be completed for every photo. Add blur regions or stickers manually before export.",
        );
      }
      setProcessingState("complete");
    } catch (caughtError) {
      if (controller.signal.aborted) return;
      setProcessingState("error");
      setError(
        caughtError instanceof Error
          ? caughtError.message
          : "The images could not be processed. Please try again.",
      );
    } finally {
      if (activeControllerRef.current === controller) {
        activeControllerRef.current = null;
      }
    }
  };

  const downloadRegularImage = async (index: number) => {
    if (!result) return;
    const isEditableCensorship =
      result.appliedFilters.length === 1 &&
      result.appliedFilters[0] === "cover-privates" &&
      Boolean(result.coverImageUrl);
    if (isEditableCensorship && placementsByImage[index] === undefined) return;
    setError("");
    setDownloadingIndex(index);
    try {
      const filename = isEditableCensorship
        ? `censored-image-${index + 1}.png`
        : `filtered-image-${index + 1}.png`;
      if (
        isEditableCensorship &&
        getCensorshipDownloadPath({
          isDebugMode,
          isEdited: Boolean(editedImages[index]),
          forceLocalCanvas,
        }) === "local-canvas"
      ) {
        await downloadEditedCensorshipImage({
          imageUrl: selectedImagePreviewUrls[index],
          stickerUrl: result.coverImageUrl as string,
          placements: placementsByImage[index],
          filename,
        });
      } else {
        await downloadImageToolResult(result.downloadUrls[index], filename);
      }
    } catch (downloadError) {
      setError(
        downloadError instanceof Error
          ? downloadError.message
          : "The image could not be downloaded. Please try again.",
      );
    } finally {
      setDownloadingIndex(null);
    }
  };

  const downloadAllRegularImages = async () => {
    if (!result || isDownloadingAll) return;
    setError("");
    setIsDownloadingAll(true);
    try {
      const isEditableCensorship =
        result.appliedFilters.length === 1 &&
        result.appliedFilters[0] === "cover-privates" &&
        Boolean(result.coverImageUrl);
      for (let index = 0; index < result.downloadUrls.length; index += 1) {
        const placements = placementsByImage[index];
        if (isEditableCensorship && placements === undefined) {
          throw new Error(
            "The image editor is still loading. Wait a moment and retry the download.",
          );
        }
        const filename = isEditableCensorship
          ? `censored-image-${index + 1}.png`
          : `filtered-image-${index + 1}.png`;
        if (
          isEditableCensorship &&
          getCensorshipDownloadPath({
            isDebugMode,
            isEdited: Boolean(editedImages[index]),
            forceLocalCanvas,
          }) === "local-canvas"
        ) {
          await downloadEditedCensorshipImage({
            imageUrl: selectedImagePreviewUrls[index],
            stickerUrl: result.coverImageUrl as string,
            placements: placements as TCensorshipStickerPlacement[],
            filename,
          });
        } else {
          await downloadImageToolResult(result.downloadUrls[index], filename);
        }
      }
    } catch (downloadError) {
      setError(
        downloadError instanceof Error
          ? downloadError.message
          : "Not all images could be downloaded. Retry the download.",
      );
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const handleRegularDownload = async () => {
    const target = downloadTarget;
    setDownloadTarget(null);
    if (target === null) return;
    if (target === "all") {
      await downloadAllRegularImages();
      return;
    }
    await downloadRegularImage(target);
  };

  const handleCancelDownload = () => {
    exportControllerRef.current?.abort();
    exportControllerRef.current = null;
    setIsHighQualityExporting(false);
    setDownloadTarget(null);
  };

  const handleHighQualityDownload = async (
    requestedTarget: Exclude<TDownloadTarget, null> | null = downloadTarget,
  ) => {
    if (
      !result ||
      !session?.clinicId ||
      requestedTarget === null ||
      isHighQualityExporting ||
      !result.appliedFilters.some(
        (filter) => filter === "cover-privates" || filter === "hide-faces",
      )
    ) {
      return;
    }

    const target = requestedTarget;
    const indices =
      target === "all"
        ? result.downloadUrls.map((_, index) => index)
        : [target];
    const controller = new AbortController();
    exportControllerRef.current?.abort();
    exportControllerRef.current = controller;
    setError("");
    setIsHighQualityExporting(true);

    try {
      const exportImages = indices.map((index) => {
        const inputS3Key = result.metadata.images[index]?.input_s3_key;
        if (!inputS3Key) {
          throw new Error(
            "The high-quality export is not ready. Wait a moment and retry.",
          );
        }

        const orderedTools = result.appliedFilters.flatMap<TOrderedImageTool>(
          (filter) => {
            if (filter === "blur-tattoos") {
              return [
                {
                  id: "blur-tattoos" as const,
                  mode: "tattoo_blur" as const,
                  blurRadius: DEFAULT_TATTOO_BLUR_RADIUS,
                },
              ];
            }
            if (filter === "cover-privates") {
              const placements = placementsByImage[index];
              if (!result.coverImageUrl || placements === undefined) {
                throw new Error(
                  "The private-area placements are still loading. Wait a moment and retry.",
                );
              }
              return [
                {
                  id: "cover-privates" as const,
                  mode: "overlay" as const,
                  assetUrl: result.coverImageUrl,
                  placements,
                },
              ];
            }
            if (filter === "remove-background") {
              return [
                {
                  id: "remove-background" as const,
                  mode: "background_remove" as const,
                  alphaThreshold: null,
                },
              ];
            }
            if (filter !== "hide-faces") return [];
            if (result.faceCensorshipMode === "blur") {
              const regions = faceBlurRegionsByImage[index];
              if (!regions?.length) {
                throw new Error(
                  "Add and review at least one face blur region before export.",
                );
              }
              return [
                {
                  id: "hide-faces" as const,
                  mode: "face_blur" as const,
                  regions,
                },
              ];
            }
            const placements = faceStickerPlacementsByImage[index];
            if (!result.coverImageUrl || !placements?.length) {
              throw new Error(
                "Add and review at least one face sticker before export.",
              );
            }
            return [
              {
                id: "hide-faces" as const,
                mode: "overlay" as const,
                assetUrl: result.coverImageUrl,
                placements,
              },
            ];
          },
        );

        return {
          input_s3_key: inputS3Key,
          operations: buildOrderedImageToolExportOperations(orderedTools),
        };
      });
      const job = await startImageToolExport({
        clinicId: session.clinicId,
        images: exportImages,
        signal: controller.signal,
      });
      const metadata = await waitForImageToolExport({
        metadataUrl: job.metadata_url,
        signal: controller.signal,
      });
      setExportHistoryRefreshKey((current) => current + 1);

      if (!metadata.success) {
        const reason = metadata.errors
          ? Object.values(metadata.errors).join(" ")
          : "The high-quality renderer did not complete this export.";
        throw new Error(`${reason} Please retry the download.`);
      }

      for (
        let outputIndex = 0;
        outputIndex < indices.length;
        outputIndex += 1
      ) {
        const imageIndex = indices[outputIndex];
        await downloadImageToolResult(
          job.download_urls[outputIndex],
          `censored-image-${imageIndex + 1}-high-quality.png`,
        );
      }
      setDownloadTarget(null);
    } catch (downloadError) {
      if (controller.signal.aborted) return;
      setError(
        downloadError instanceof Error
          ? downloadError.message
          : "The high-quality export could not be downloaded. Please retry.",
      );
      setDownloadTarget(null);
    } finally {
      if (exportControllerRef.current === controller) {
        setIsHighQualityExporting(false);
        exportControllerRef.current = null;
      }
    }
  };

  const getStatusText = () => {
    if (processingState === "uploading") {
      return `Uploading images — ${uploadProgress}%`;
    }
    if (processingState === "queued") return "Starting secure processing…";
    if (processingState === "processing") {
      return isFaceCensorshipSelected
        ? "Detecting censorship regions for review…"
        : "Applying the selected AI filters…";
    }
    return "";
  };

  const isEditableCensorshipResult =
    !!result &&
    (result.appliedFilters.includes("hide-faces") ||
      result.appliedFilters.includes("cover-privates"));
  const editorsReady =
    !!result &&
    (!isEditableCensorshipResult ||
      result.downloadUrls.every(
        (_, index) =>
          (!result.appliedFilters.includes("cover-privates") ||
            placementsByImage[index] !== undefined) &&
          (!result.appliedFilters.includes("hide-faces") ||
            (result.faceCensorshipMode === "blur"
              ? Boolean(faceBlurRegionsByImage[index]?.length)
              : Boolean(faceStickerPlacementsByImage[index]?.length))),
      ));

  const handleDownloadRequest = (index: number) => {
    const requiresCompositeExport =
      result?.appliedFilters.includes("hide-faces") ||
      Boolean(
        result?.appliedFilters.includes("cover-privates") &&
          result.appliedFilters.some((filter) => filter !== "cover-privates"),
      );
    if (requiresCompositeExport) {
      setDownloadTarget(index);
      void handleHighQualityDownload(index);
      return;
    }
    if (isEditableCensorshipResult) {
      setDownloadTarget(index);
      return;
    }
    void downloadRegularImage(index);
  };

  const handleDownloadAllRequest = () => {
    const requiresCompositeExport =
      result?.appliedFilters.includes("hide-faces") ||
      Boolean(
        result?.appliedFilters.includes("cover-privates") &&
          result.appliedFilters.some((filter) => filter !== "cover-privates"),
      );
    if (requiresCompositeExport) {
      setDownloadTarget("all");
      void handleHighQualityDownload("all");
      return;
    }
    if (isEditableCensorshipResult) {
      setDownloadTarget("all");
      return;
    }
    void downloadAllRegularImages();
  };

  const handlePlacementsChange = React.useCallback(
    (
      index: number,
      placements: TCensorshipStickerPlacement[],
      isEdited: boolean,
    ) => {
      setPlacementsByImage((current) => {
        if (current[index] === placements) return current;
        return { ...current, [index]: placements };
      });
      setEditedImages((current) =>
        current[index] === isEdited
          ? current
          : { ...current, [index]: isEdited },
      );
    },
    [],
  );

  const handleFaceStickerPlacementsChange = React.useCallback(
    (
      index: number,
      placements: TCensorshipStickerPlacement[],
      isEdited: boolean,
    ) => {
      setFaceStickerPlacementsByImage((current) => ({
        ...current,
        [index]: placements,
      }));
      setEditedImages((current) => ({ ...current, [index]: isEdited }));
    },
    [],
  );

  const handleFaceBlurRegionsChange = React.useCallback(
    (index: number, regions: TFaceBlurRegion[], isEdited: boolean) => {
      setFaceBlurRegionsByImage((current) => ({
        ...current,
        [index]: regions,
      }));
      setEditedImages((current) => ({ ...current, [index]: isEdited }));
    },
    [],
  );

  const handleExitDebugMode = () => {
    const nextParams = new URLSearchParams(searchParams.toString());
    nextParams.delete("debug");
    setSelectedImages([]);
    setResult(null);
    setPlacementsByImage({});
    setFaceStickerPlacementsByImage({});
    setFaceBlurRegionsByImage({});
    setEditedImages({});
    setProcessingState("idle");
    setDebugLoadState("idle");
    setDebugLoadError("");
    setSearchParams(nextParams, { replace: true });
  };

  const handleRequestStickerDelete = (assetId: number) => {
    const sticker = clinicStickers.find((item) => item.asset_id === assetId);
    if (sticker) setStickerToDelete(sticker);
  };

  return (
    <ScrollView
      style={styles.page}
      contentContainerStyle={styles.pageContent}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.contentContainer}>
        <View style={[styles.header, isMobile && styles.mobileHeader]}>
          <View style={styles.headerIcon}>
            <ImageFilterIcon
              name="image-tools"
              color={tokens.color.accentDark}
              size={tokens.fontSize.xxxl}
            />
          </View>
          <View style={styles.headerCopy}>
            <Text style={styles.eyebrow}>IMAGE TOOLS</Text>
            <TitleText
              text="Transform patient photos with AI"
              accessibilityRole="header"
              style={styles.pageTitle}
            />
            <Text style={styles.pageDescription}>
              Upload photos and apply one or more secure filters before using
              them in patient communication, clinical review, or marketing.
            </Text>
          </View>
        </View>

        {isDebugMode ? (
          <View
            accessibilityLabel="Image Tools debug mode"
            style={[styles.debugPanel, isMobile && styles.mobileStack]}
          >
            <View style={styles.debugPanelCopy}>
              <View style={styles.debugBadge}>
                <Text style={styles.debugBadgeText}>DEBUG MODE</Text>
              </View>
              <Text accessibilityRole="header" style={styles.sectionTitle}>
                Simulated censorship preview
              </Text>
              <Text style={styles.sectionDescription}>
                This uses the Before/After front sample with two simulated
                detection regions. No censorship API request is made.
              </Text>
              {debugLoadState === "loading" ? (
                <View
                  accessibilityLiveRegion="polite"
                  style={styles.debugStatus}
                >
                  <ActivityIndicator size="small" color={tokens.color.accent} />
                  <Text style={styles.sectionDescription}>
                    Loading the debug sample…
                  </Text>
                </View>
              ) : null}
              {debugLoadState === "error" ? (
                <Text
                  accessibilityLiveRegion="assertive"
                  accessibilityRole="alert"
                  style={styles.errorText}
                >
                  {debugLoadError}
                </Text>
              ) : null}
            </View>
            <View
              style={[styles.debugActions, isMobile && styles.mobileActions]}
            >
              <SecondaryButton
                text={
                  debugLoadState === "error"
                    ? "Retry Debug Sample"
                    : "Reload Sample"
                }
                onPress={() => setDebugReloadKey((current) => current + 1)}
                loading={debugLoadState === "loading"}
                disabled={debugLoadState === "loading"}
                fullWidth={isMobile}
              />
              <SecondaryButton
                text="Exit Debug Mode"
                onPress={handleExitDebugMode}
                disabled={debugLoadState === "loading"}
                fullWidth={isMobile}
              />
            </View>
          </View>
        ) : (
          <View style={styles.toolCard}>
            <View style={styles.sectionHeader}>
              <View style={styles.stepBadge}>
                <Text style={styles.stepBadgeText}>1</Text>
              </View>
              <View style={styles.sectionHeaderCopy}>
                <Text accessibilityRole="header" style={styles.sectionTitle}>
                  Add photos
                </Text>
                <Text style={styles.sectionDescription}>
                  Upload PNG, JPEG, HEIC, or HEIF files up to 50 MB each. You
                  can process up to {MAX_IMAGES} photos in one batch.
                </Text>
              </View>
            </View>

            <MultiImageUpload
              images={selectedImages}
              onImagesChange={handleSelectedImagesChange}
              maxImages={MAX_IMAGES}
              maxImageHeight={MAX_IMAGE_HEIGHT}
              disabled={isBusy}
              emptyHint="Select multiple photos to process them together"
              inputAccessibilityLabel="Add photos for AI filtering"
              testIDPrefix="image-tools"
              previewsInsideDropZone
              onPreparingChange={setIsPreparingImages}
            />

            <ImageToolsFilterPicker
              selectedFilters={selectedFilters}
              disabled={isBusy}
              isMobile={isMobile}
              onToggleFilter={toggleFilter}
            />

            {isFaceCensorshipSelected ? (
              <FaceCensorshipModePicker
                mode={faceCensorshipMode}
                disabled={isBusy}
                onChange={(mode) => {
                  setFaceCensorshipMode(mode);
                  setResult(null);
                  setFaceStickerPlacementsByImage({});
                  setFaceBlurRegionsByImage({});
                  setProcessingState("idle");
                  setError("");
                }}
              />
            ) : null}

            {needsStickerPicker ? (
              <ImageToolsStickerLibrary
                coverOptions={coverOptions}
                selectedCoverId={selectedCoverId}
                stickerToDelete={stickerToDelete}
                isBusy={isBusy}
                isMobile={isMobile}
                isLoading={isStickerLibraryLoading}
                isAdding={isAddingSticker}
                deletingAssetId={deletingStickerId}
                error={stickerLibraryError}
                onAddSticker={handleAddSticker}
                onSelectCover={setSelectedCoverId}
                onRequestDelete={handleRequestStickerDelete}
                onCancelDelete={() => setStickerToDelete(null)}
                onConfirmDelete={handleDeleteSticker}
                onRetry={fetchStickerLibrary}
                purpose={
                  isCoverFilterSelected && isFaceCensorshipSelected
                    ? "both"
                    : isFaceCensorshipSelected
                      ? "faces"
                      : "private-areas"
                }
              />
            ) : null}

            <ImageToolsProcessingStatus
              error={error}
              isBusy={isBusy}
              isMobile={isMobile}
              isUploading={processingState === "uploading"}
              uploadProgress={uploadProgress}
              statusText={getStatusText()}
              applyDisabled={
                !selectedImages.length ||
                !selectedFilters.length ||
                isPreparingImages
              }
              onApply={handleGenerate}
            />
          </View>
        )}

        {result ? (
          <ImageToolsResults
            result={result}
            imagePreviewUrls={selectedImagePreviewUrls}
            placementsByImage={placementsByImage}
            faceStickerPlacementsByImage={faceStickerPlacementsByImage}
            faceBlurRegionsByImage={faceBlurRegionsByImage}
            downloadingIndex={downloadingIndex}
            isDownloadingAll={isDownloadingAll}
            isHighQualityExporting={isHighQualityExporting}
            downloadDialogVisible={downloadTarget !== null}
            downloadDialogIsAll={downloadTarget === "all"}
            editorsReady={editorsReady}
            isDebugMode={isDebugMode}
            showRegionDebugLabels={showRegionDebugLabels}
            isMobile={isMobile}
            viewportWidth={width}
            onDownload={handleDownloadRequest}
            onDownloadAll={handleDownloadAllRequest}
            onCancelDownload={handleCancelDownload}
            onRegularDownload={handleRegularDownload}
            onHighQualityDownload={() => void handleHighQualityDownload()}
            onPlacementsChange={handlePlacementsChange}
            onFaceStickerPlacementsChange={handleFaceStickerPlacementsChange}
            onFaceBlurRegionsChange={handleFaceBlurRegionsChange}
          />
        ) : null}

        <ImageToolsExportHistory
          session={session}
          refreshKey={exportHistoryRefreshKey}
          viewportWidth={width}
        />
      </View>
    </ScrollView>
  );
};

export default ImageToolsPage;
